import React, { useEffect, useState } from 'react';
import { Loader2, Check, Zap, TrendingUp, Radio, Bell, Settings, Lock, Send, MessageCircle, Filter } from 'lucide-react';
import { apiGet, apiPut, adminPost, adminGet } from '../data/api';
import t from '../i18n/it';

const mono = { fontFamily: 'JetBrains Mono, monospace' };
const heading = { fontFamily: 'Outfit, sans-serif' };

function ToggleSwitch({ checked, onChange, disabled, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex items-center w-10 h-5 rounded-full transition-colors focus:outline-none ${
        disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
      } ${checked ? 'bg-emerald-500' : 'bg-zinc-700'}`}
    >
      <span
        className={`absolute w-4 h-4 bg-white rounded-full shadow transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

function Section({ icon: Icon, iconColor, title, desc, children }) {
  return (
    <div
      className="rounded-sm overflow-hidden"
      style={{ background: '#18181B', border: '1px solid #27272A' }}
    >
      <div className="px-5 py-4 flex items-center gap-3" style={{ borderBottom: '1px solid #27272A' }}>
        {Icon && (
          <div className={`w-7 h-7 rounded-sm flex items-center justify-center ${iconColor || 'bg-zinc-800'}`}>
            <Icon size={13} className="text-current" />
          </div>
        )}
        <div>
          <h2 className="font-semibold text-zinc-200 text-sm" style={heading}>{title}</h2>
          {desc && <p className="text-xs text-zinc-500 mt-0.5">{desc}</p>}
        </div>
      </div>
      <div className="divide-y" style={{ borderColor: '#1F1F22' }}>
        {children}
      </div>
    </div>
  );
}

function SettingRow({ label, desc, right, badge }) {
  return (
    <div className="flex items-center justify-between px-5 py-4 gap-4">
      <div>
        <div className="text-sm font-medium text-zinc-200 flex items-center gap-2">
          {label}
          {badge && (
            <span className="px-1.5 py-0.5 text-[10px] rounded bg-zinc-700/60 text-zinc-500 uppercase tracking-wider">
              {badge}
            </span>
          )}
        </div>
        {desc && <div className="text-xs text-zinc-500 mt-0.5">{desc}</div>}
      </div>
      <div className="flex-shrink-0">{right}</div>
    </div>
  );
}

function SliderInput({ value, onChange, min, max, step, format, testId }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm font-medium text-emerald-400 w-14 text-right" style={mono}>
        {format ? format(value) : value}
      </span>
      <input
        data-testid={testId}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-32 accent-emerald-500"
        style={{ accentColor: '#10B981' }}
      />
    </div>
  );
}

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [tgStatus, setTgStatus] = useState(null);
  const [tgTesting, setTgTesting] = useState(false);
  const [tgTestResult, setTgTestResult] = useState(null);
  const [pickFilters, setPickFilters] = useState(null);
  const [pfSaving, setPfSaving] = useState(false);
  const [pfSaved, setPfSaved] = useState(false);
  const [dynamicEv, setDynamicEv] = useState(null);
  const [dynSaving, setDynSaving] = useState(false);
  const [dynSaved, setDynSaved] = useState(false);
  const [deepUd, setDeepUd] = useState(null);
  const [duSaving, setDuSaving] = useState(false);
  const [duSaved, setDuSaved] = useState(false);
  const adminPin = 'pointedge2024';

  useEffect(() => {
    apiGet('/settings')
      .then(setSettings)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    adminGet('/tt/admin/telegram-status', adminPin)
      .then(setTgStatus)
      .catch(() => {});
    adminGet('/tt/admin/pick-filters', adminPin)
      .then((d) => {
        setPickFilters(d);
        if (d?.dynamic_ev) setDynamicEv(d.dynamic_ev);
        if (d?.deep_underdog) setDeepUd(d.deep_underdog);
      })
      .catch(() => {});
  }, []);

  const update = (key, value) => setSettings((s) => ({ ...s, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const updated = await apiPut('/settings', {
        minimum_ev_threshold_prematch: settings.minimum_ev_threshold_prematch,
        minimum_ev_threshold_live: settings.minimum_ev_threshold_live,
        minimum_confidence_score: settings.minimum_confidence_score,
        minimum_momentum_score: settings.minimum_momentum_score,
        enable_prematch_alerts: settings.enable_prematch_alerts,
        enable_live_alerts: settings.enable_live_alerts,
        enable_in_app_alerts: settings.enable_in_app_alerts,
        enable_telegram_alerts: settings.enable_telegram_alerts,
        enable_email_alerts: settings.enable_email_alerts,
        favorite_sport: settings.favorite_sport,
      });
      setSettings(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(t.settings.save_error);
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="flex items-center gap-2 justify-center py-16 text-zinc-500">
        <Loader2 size={18} className="animate-spin" />
        <span>{t.common.loading}</span>
      </div>
    );

  return (
    <div className="space-y-5 fade-in max-w-2xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-zinc-50 tracking-tight" style={heading}>
          {t.settings.title}
        </h1>
        <p className="text-zinc-400 mt-1 text-sm">{t.settings.subtitle}</p>
      </div>

      {/* 1. PREMATCH SETTINGS */}
      <Section
        icon={TrendingUp}
        iconColor="bg-emerald-500/10 text-emerald-400"
        title={t.settings.section_prematch}
        desc={t.settings.section_prematch_desc}
      >
        <SettingRow
          label={t.settings.min_ev_prematch}
          desc={t.settings.min_ev_prematch_desc}
          right={
            <SliderInput
              testId="slider-min-ev-prematch"
              value={settings?.minimum_ev_threshold_prematch || 0.05}
              onChange={(v) => update('minimum_ev_threshold_prematch', v)}
              min={0.01} max={0.20} step={0.01}
              format={(v) => `${(v * 100).toFixed(0)}%`}
            />
          }
        />
        <SettingRow
          label={t.settings.min_confidence}
          desc={t.settings.min_confidence_desc}
          right={
            <SliderInput
              testId="slider-min-confidence"
              value={settings?.minimum_confidence_score || 0.65}
              onChange={(v) => update('minimum_confidence_score', v)}
              min={0.10} max={1.0} step={0.05}
              format={(v) => `${(v * 100).toFixed(0)}%`}
            />
          }
        />
        <SettingRow
          label={t.settings.prematch_alerts}
          desc={t.settings.prematch_alerts_desc}
          right={
            <ToggleSwitch
              testId="toggle-prematch-alerts"
              checked={settings?.enable_prematch_alerts}
              onChange={(v) => update('enable_prematch_alerts', v)}
            />
          }
        />
      </Section>

      {/* 2. LIVE SETTINGS */}
      <Section
        icon={Radio}
        iconColor="bg-amber-500/10 text-amber-400"
        title={t.settings.section_live}
        desc={t.settings.section_live_desc}
      >
        <SettingRow
          label={t.settings.min_ev_live}
          desc={t.settings.min_ev_live_desc}
          right={
            <SliderInput
              testId="slider-min-ev-live"
              value={settings?.minimum_ev_threshold_live || 0.08}
              onChange={(v) => update('minimum_ev_threshold_live', v)}
              min={0.01} max={0.25} step={0.01}
              format={(v) => `${(v * 100).toFixed(0)}%`}
            />
          }
        />
        <SettingRow
          label={t.settings.min_momentum}
          desc={t.settings.min_momentum_desc}
          right={
            <SliderInput
              testId="slider-min-momentum"
              value={settings?.minimum_momentum_score || 50}
              onChange={(v) => update('minimum_momentum_score', v)}
              min={10} max={100} step={5}
              format={(v) => v.toFixed(0)}
            />
          }
        />
        <SettingRow
          label={t.settings.live_alerts}
          desc={t.settings.live_alerts_desc}
          right={
            <ToggleSwitch
              testId="toggle-live-alerts"
              checked={settings?.enable_live_alerts}
              onChange={(v) => update('enable_live_alerts', v)}
            />
          }
        />
      </Section>

      {/* 3. NOTIFICATION SETTINGS */}
      <Section
        icon={Bell}
        iconColor="bg-blue-500/10 text-blue-400"
        title={t.settings.notifications_section}
        desc={t.settings.notifications_section_desc}
      >
        <SettingRow
          label={t.settings.in_app_alerts}
          desc={t.settings.in_app_alerts_desc}
          right={
            <ToggleSwitch
              testId="toggle-in-app-alerts"
              checked={settings?.enable_in_app_alerts}
              onChange={(v) => update('enable_in_app_alerts', v)}
            />
          }
        />
        <SettingRow
          label="Telegram Alert"
          desc="Ricevi i pick EV+ direttamente su Telegram"
          right={
            <div className="flex items-center gap-2">
              {tgStatus?.enabled && (
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              )}
              <span className="text-xs text-zinc-500" style={mono}>
                {tgStatus?.enabled ? 'Attivo' : 'Off'}
              </span>
            </div>
          }
        />
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center gap-3">
            <button
              data-testid="telegram-test-btn"
              onClick={async () => {
                setTgTesting(true);
                setTgTestResult(null);
                try {
                  const r = await adminPost('/tt/admin/telegram-test', adminPin);
                  setTgTestResult(r.sent ? 'Inviato!' : 'Errore invio');
                } catch {
                  setTgTestResult('Errore');
                }
                setTgTesting(false);
              }}
              disabled={tgTesting}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors"
              style={{ background: '#27272A', color: '#a1a1aa', border: '1px solid #3f3f46' }}
            >
              {tgTesting ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Test Alert
            </button>
            <button
              data-testid="telegram-send-now-btn"
              onClick={async () => {
                setTgTesting(true);
                setTgTestResult(null);
                try {
                  const r = await adminPost('/tt/admin/telegram-send-now', adminPin);
                  setTgTestResult(`Inviati: ${r.new_alerts_sent || 0} nuovi alert`);
                  adminGet('/tt/admin/telegram-status', adminPin).then(setTgStatus).catch(() => {});
                } catch {
                  setTgTestResult('Errore');
                }
                setTgTesting(false);
              }}
              disabled={tgTesting}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors"
              style={{ background: '#27272A', color: '#a1a1aa', border: '1px solid #3f3f46' }}
            >
              {tgTesting ? <Loader2 size={12} className="animate-spin" /> : <MessageCircle size={12} />}
              Invia Ora
            </button>
            {tgTestResult && (
              <span className="text-xs text-emerald-400">{tgTestResult}</span>
            )}
          </div>
          {tgStatus && (
            <div className="text-xs text-zinc-500 flex gap-4">
              <span>Alert inviati: <strong className="text-zinc-400">{tgStatus.total_alerts_sent}</strong></span>
              <span>Min EV: <strong className="text-zinc-400">{tgStatus.min_ev}%</strong></span>
            </div>
          )}
        </div>
        <SettingRow
          label={t.settings.email_alerts}
          desc={t.settings.email_alerts_desc}
          badge={t.settings.coming_soon}
          right={
            <div className="flex items-center gap-2">
              <Lock size={12} className="text-zinc-600" />
              <ToggleSwitch
                testId="toggle-email-alerts"
                checked={settings?.enable_email_alerts}
                onChange={(v) => update('enable_email_alerts', v)}
                disabled
              />
            </div>
          }
        />
      </Section>

      {/* 4. PICK FILTERS (Confidence + EV per side) */}
      <Section
        icon={Filter}
        iconColor="bg-violet-500/10 text-violet-400"
        title="Pick Filters"
        desc="Filtra i pick per confidence e soglia EV separata per favorito/underdog"
      >
        <SettingRow
          label="Filtro Confidence"
          desc="Scarta pick sotto il livello di confidence minimo"
          right={
            <ToggleSwitch
              testId="toggle-confidence-filter"
              checked={!!pickFilters?.confidence_filter?.enabled}
              onChange={(v) =>
                setPickFilters((pf) => ({
                  ...pf,
                  confidence_filter: { ...(pf?.confidence_filter || {}), enabled: v, min_level: pf?.confidence_filter?.min_level || 'media' },
                }))
              }
            />
          }
        />
        <SettingRow
          label="Livello minimo"
          desc="molto_alta ≥70% · alta ≥55% · media ≥40%"
          right={
            <select
              data-testid="select-min-confidence-level"
              value={pickFilters?.confidence_filter?.min_level || 'media'}
              onChange={(e) =>
                setPickFilters((pf) => ({
                  ...pf,
                  confidence_filter: { ...(pf?.confidence_filter || {}), min_level: e.target.value, enabled: pf?.confidence_filter?.enabled ?? false },
                }))
              }
              className="px-3 py-2 text-sm rounded-sm text-zinc-300 focus:outline-none focus:ring-1 focus:ring-violet-500"
              style={{ background: '#0D0D0F', border: '1px solid #27272A' }}
            >
              <option value="molto_alta">Molto Alta</option>
              <option value="alta">Alta</option>
              <option value="media">Media</option>
            </select>
          }
        />
        <SettingRow
          label="EV minimo favorito (P1)"
          desc={`Quota < ${pickFilters?.ev_thresholds?.underdog_odds_threshold ?? 2.0}`}
          right={
            <SliderInput
              testId="slider-p1-min-ev"
              value={pickFilters?.ev_thresholds?.p1_min_ev ?? 5.0}
              onChange={(v) =>
                setPickFilters((pf) => ({
                  ...pf,
                  ev_thresholds: { ...(pf?.ev_thresholds || {}), p1_min_ev: v },
                }))
              }
              min={0} max={20} step={0.5}
              format={(v) => `+${v.toFixed(1)}%`}
            />
          }
        />
        <SettingRow
          label="EV minimo underdog (P2)"
          desc={`Quota ≥ ${pickFilters?.ev_thresholds?.underdog_odds_threshold ?? 2.0}`}
          right={
            <SliderInput
              testId="slider-underdog-min-ev"
              value={pickFilters?.ev_thresholds?.underdog_min_ev ?? 8.0}
              onChange={(v) =>
                setPickFilters((pf) => ({
                  ...pf,
                  ev_thresholds: { ...(pf?.ev_thresholds || {}), underdog_min_ev: v },
                }))
              }
              min={0} max={25} step={0.5}
              format={(v) => `+${v.toFixed(1)}%`}
            />
          }
        />
        <SettingRow
          label="Soglia quota underdog"
          desc="Quota da cui la selezione è considerata P2/underdog"
          right={
            <SliderInput
              testId="slider-underdog-odds-threshold"
              value={pickFilters?.ev_thresholds?.underdog_odds_threshold ?? 2.0}
              onChange={(v) =>
                setPickFilters((pf) => ({
                  ...pf,
                  ev_thresholds: { ...(pf?.ev_thresholds || {}), underdog_odds_threshold: v },
                }))
              }
              min={1.5} max={3.0} step={0.05}
              format={(v) => v.toFixed(2)}
            />
          }
        />
        <div className="px-5 py-4">
          <button
            data-testid="save-pick-filters-btn"
            onClick={async () => {
              if (!pickFilters) return;
              setPfSaving(true);
              setPfSaved(false);
              try {
                const conf = pickFilters.confidence_filter || {};
                const ev = pickFilters.ev_thresholds || {};
                await adminPost('/tt/admin/pick-filters/confidence', adminPin, {
                  enabled: !!conf.enabled,
                  min_level: conf.min_level || 'media',
                });
                await adminPost('/tt/admin/pick-filters/ev-thresholds', adminPin, {
                  p1_min_ev: Number(ev.p1_min_ev ?? 5.0),
                  underdog_min_ev: Number(ev.underdog_min_ev ?? 8.0),
                  underdog_odds_threshold: Number(ev.underdog_odds_threshold ?? 2.0),
                });
                const fresh = await adminGet('/tt/admin/pick-filters', adminPin);
                setPickFilters(fresh);
                setPfSaved(true);
                setTimeout(() => setPfSaved(false), 2500);
              } catch {
                /* noop */
              }
              setPfSaving(false);
            }}
            disabled={pfSaving || !pickFilters}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50"
            style={{ background: '#27272A', color: '#a1a1aa', border: '1px solid #3f3f46' }}
          >
            {pfSaving ? <Loader2 size={12} className="animate-spin" /> : pfSaved ? <Check size={12} className="text-emerald-400" /> : <Filter size={12} />}
            {pfSaved ? 'Salvato' : 'Salva Pick Filters'}
          </button>
        </div>
      </Section>

      {/* 4b. DYNAMIC EV BY CONFIDENCE */}
      <Section
        icon={Filter}
        iconColor="bg-fuchsia-500/10 text-fuchsia-400"
        title="Threshold Dinamici per Confidence"
        desc="EV minimo variabile per livello di confidence — sfrutta la calibrazione per volume intelligente"
      >
        <SettingRow
          label="Attiva dinamici"
          desc="Se ON, sostituisce i threshold statici P1/P2 sopra (solo MW)"
          right={
            <ToggleSwitch
              testId="toggle-dynamic-ev"
              checked={!!dynamicEv?.enabled}
              onChange={(v) =>
                setDynamicEv((d) => ({ ...(d || { levels: {} }), enabled: v }))
              }
            />
          }
        />
        {['molto_alta', 'alta', 'media', 'bassa'].map((lvl) => {
          const labels = {
            molto_alta: { title: 'Molto Alta', desc: 'confidence ≥70%', accent: 'text-emerald-400' },
            alta:       { title: 'Alta',        desc: 'confidence 55-70%', accent: 'text-teal-400' },
            media:      { title: 'Media',       desc: 'confidence 40-55%', accent: 'text-amber-400' },
            bassa:      { title: 'Bassa',       desc: 'confidence <40%',   accent: 'text-red-400' },
          }[lvl];
          const cur = (dynamicEv?.levels || {})[lvl] || { p1_min_ev: 0, p2_min_ev: 0 };
          return (
            <div key={lvl} className="px-5 py-4 border-b border-zinc-800/60 last:border-b-0">
              <div className={`text-xs font-semibold mb-3 ${labels.accent}`}>
                {labels.title} <span className="text-zinc-500 font-normal">— {labels.desc}</span>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-zinc-500 w-6">P1</span>
                  <SliderInput
                    testId={`slider-dyn-p1-${lvl}`}
                    value={cur.p1_min_ev ?? 0}
                    onChange={(v) =>
                      setDynamicEv((d) => ({
                        ...(d || { enabled: false }),
                        levels: { ...(d?.levels || {}), [lvl]: { ...cur, p1_min_ev: v } },
                      }))
                    }
                    min={0} max={20} step={0.5}
                    format={(v) => `+${v.toFixed(1)}%`}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-zinc-500 w-6">P2</span>
                  <SliderInput
                    testId={`slider-dyn-p2-${lvl}`}
                    value={cur.p2_min_ev ?? 0}
                    onChange={(v) =>
                      setDynamicEv((d) => ({
                        ...(d || { enabled: false }),
                        levels: { ...(d?.levels || {}), [lvl]: { ...cur, p2_min_ev: v } },
                      }))
                    }
                    min={0} max={25} step={0.5}
                    format={(v) => `+${v.toFixed(1)}%`}
                  />
                </div>
              </div>
            </div>
          );
        })}
        <div className="px-5 py-4">
          <button
            data-testid="save-dynamic-ev-btn"
            onClick={async () => {
              if (!dynamicEv) return;
              setDynSaving(true);
              setDynSaved(false);
              try {
                await adminPost('/tt/admin/pick-filters/dynamic-ev', adminPin, {
                  enabled: !!dynamicEv.enabled,
                  levels: dynamicEv.levels || {},
                });
                const fresh = await adminGet('/tt/admin/pick-filters', adminPin);
                if (fresh?.dynamic_ev) setDynamicEv(fresh.dynamic_ev);
                setDynSaved(true);
                setTimeout(() => setDynSaved(false), 2500);
              } catch { /* noop */ }
              setDynSaving(false);
            }}
            disabled={dynSaving || !dynamicEv}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50"
            style={{ background: '#27272A', color: '#a1a1aa', border: '1px solid #3f3f46' }}
          >
            {dynSaving ? <Loader2 size={12} className="animate-spin" /> : dynSaved ? <Check size={12} className="text-emerald-400" /> : <Filter size={12} />}
            {dynSaved ? 'Salvato' : 'Salva Dinamici'}
          </button>
        </div>
      </Section>

      {/* 4c. DEEP UNDERDOG FILTER */}
      <Section
        icon={TrendingUp}
        iconColor="bg-rose-500/10 text-rose-400"
        title="Deep Underdog Filter"
        desc="Sfrutta il bias mercato su quote alte (>2.50). Richiede confidence minima per compensare sottostima modello"
      >
        <SettingRow
          label="Attiva Deep Underdog"
          desc="Override regole P2 standard per quote ≥ soglia"
          right={
            <ToggleSwitch
              testId="toggle-deep-underdog"
              checked={!!deepUd?.enabled}
              onChange={(v) => setDeepUd((d) => ({ ...(d || {}), enabled: v }))}
            />
          }
        />
        <SettingRow
          label="Soglia quota minima"
          desc="Sopra questa quota applico regole deep underdog"
          right={
            <SliderInput
              testId="slider-du-odds"
              value={deepUd?.odds_threshold ?? 2.5}
              onChange={(v) => setDeepUd((d) => ({ ...(d || {}), odds_threshold: v }))}
              min={2.0} max={4.0} step={0.1}
              format={(v) => v.toFixed(2)}
            />
          }
        />
        <SettingRow
          label="Confidence minima richiesta"
          desc="Livello minimo per accettare un deep underdog"
          right={
            <select
              data-testid="select-du-confidence"
              value={deepUd?.min_confidence || 'alta'}
              onChange={(e) => setDeepUd((d) => ({ ...(d || {}), min_confidence: e.target.value }))}
              className="px-3 py-2 text-sm rounded-sm text-zinc-300 focus:outline-none focus:ring-1 focus:ring-rose-500"
              style={{ background: '#0D0D0F', border: '1px solid #27272A' }}
            >
              <option value="molto_alta">Molto Alta</option>
              <option value="alta">Alta</option>
              <option value="media">Media</option>
            </select>
          }
        />
        <SettingRow
          label="EV minimo deep underdog"
          desc="Threshold abbassato per sfruttare bias mercato"
          right={
            <SliderInput
              testId="slider-du-ev"
              value={deepUd?.min_ev ?? 5.0}
              onChange={(v) => setDeepUd((d) => ({ ...(d || {}), min_ev: v }))}
              min={0} max={15} step={0.5}
              format={(v) => `+${v.toFixed(1)}%`}
            />
          }
        />
        <div className="px-5 py-4">
          <button
            data-testid="save-deep-underdog-btn"
            onClick={async () => {
              if (!deepUd) return;
              setDuSaving(true);
              setDuSaved(false);
              try {
                await adminPost('/tt/admin/pick-filters/deep-underdog', adminPin, {
                  enabled: !!deepUd.enabled,
                  odds_threshold: Number(deepUd.odds_threshold ?? 2.5),
                  min_confidence: deepUd.min_confidence || 'alta',
                  min_ev: Number(deepUd.min_ev ?? 5.0),
                });
                const fresh = await adminGet('/tt/admin/pick-filters', adminPin);
                if (fresh?.deep_underdog) setDeepUd(fresh.deep_underdog);
                setDuSaved(true);
                setTimeout(() => setDuSaved(false), 2500);
              } catch { /* noop */ }
              setDuSaving(false);
            }}
            disabled={duSaving || !deepUd}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50"
            style={{ background: '#27272A', color: '#a1a1aa', border: '1px solid #3f3f46' }}
          >
            {duSaving ? <Loader2 size={12} className="animate-spin" /> : duSaved ? <Check size={12} className="text-emerald-400" /> : <TrendingUp size={12} />}
            {duSaved ? 'Salvato' : 'Salva Deep Underdog'}
          </button>
        </div>
      </Section>

      {/* 5. PREFERENCES */}
      <Section
        icon={Settings}
        iconColor="bg-zinc-700/60 text-zinc-400"
        title={t.settings.section_preferences}
        desc={t.settings.section_preferences_desc}
      >
        <SettingRow
          label={t.settings.favorite_sport}
          desc={t.settings.favorite_sport_desc}
          right={
            <select
              data-testid="select-favorite-sport"
              value={settings?.favorite_sport || ''}
              onChange={(e) => update('favorite_sport', e.target.value || null)}
              className="px-3 py-2 text-sm rounded-sm text-zinc-300 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              style={{ background: '#0D0D0F', border: '1px solid #27272A' }}
            >
              <option value="">{t.common.all}</option>
              <option value="tennis">{t.common.tennis}</option>
              <option value="table_tennis">{t.common.table_tennis}</option>
            </select>
          }
        />
        <SettingRow
          label={t.settings.version}
          right={<span className="text-xs text-zinc-500" style={mono}>v2.5.0</span>}
        />
        <SettingRow
          label={t.settings.engine_prematch}
          right={
            <span className="flex items-center gap-1.5 text-xs text-emerald-400">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              {t.settings.status_active}
            </span>
          }
        />
        <SettingRow
          label={t.settings.engine_live}
          right={
            <span className="flex items-center gap-1.5 text-xs text-emerald-400">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 live-dot" />
              {t.settings.status_active}
            </span>
          }
        />
      </Section>

      {/* Save */}
      {error && <div className="text-red-400 text-sm" data-testid="settings-error">{error}</div>}
      <button
        data-testid="save-settings-btn"
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 px-5 py-2.5 rounded-sm font-medium text-sm transition-colors disabled:opacity-50"
        style={{ background: '#10B981', color: '#000' }}
        onMouseEnter={(e) => !saving && (e.currentTarget.style.background = '#059669')}
        onMouseLeave={(e) => !saving && (e.currentTarget.style.background = '#10B981')}
      >
        {saving ? (
          <Loader2 size={15} className="animate-spin" />
        ) : saved ? (
          <Check size={15} />
        ) : (
          <Zap size={15} />
        )}
        {saved ? t.settings.save_success : t.settings.save_btn}
      </button>
    </div>
  );
}
